"use client";

import * as React from "react";
import { isRenderableImageSrc } from "components/ui/v2/avatar";
import Image from "next/image";

export interface FeaturedListItemV2Props {
  title: string;
  description?: string;
  imageUrl?: string | null;
  imageFallback?: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: () => void;
  "aria-label"?: string;
}

/**
 * FeaturedListItemV2 — single rail cell: media left + title/meta right.
 * Solid content (§5.4); not a §6.6 CRUD card.
 */
export function FeaturedListItemV2({
  title,
  description,
  imageUrl,
  imageFallback,
  meta,
  onClick,
  "aria-label": ariaLabel,
}: FeaturedListItemV2Props) {
  const media = isRenderableImageSrc(imageUrl) ? (
    <Image
      src={imageUrl}
      alt=""
      fill
      className="object-cover"
      sizes="80px"
    />
  ) : (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-50 via-sky-50 to-fuchsia-50 dark:from-indigo-950/70 dark:via-sky-950/40 dark:to-fuchsia-950/50">
      {imageFallback}
    </div>
  );

  const body = (
    <>
      <div className="relative w-20 shrink-0 overflow-hidden">{media}</div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5 p-3">
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-slate-800 dark:text-zinc-100">
          {title}
        </p>
        {description ? (
          <p className="line-clamp-2 text-[11px] leading-snug text-slate-600 dark:text-zinc-300">
            {description}
          </p>
        ) : null}
        {meta ? (
          <div className="flex flex-wrap items-center gap-1">{meta}</div>
        ) : null}
      </div>
    </>
  );

  const shellClassName =
    "flex min-h-[5rem] w-[min(100%,20rem)] shrink-0 snap-start items-stretch overflow-hidden rounded-2xl border border-slate-200/70 bg-white text-left shadow-sm transition hover:border-indigo-300/60 hover:shadow-md dark:border-zinc-800/80 dark:bg-zinc-950 dark:hover:border-indigo-500/40";

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? title}
        className={shellClassName}
      >
        {body}
      </button>
    );
  }

  return <div className={shellClassName}>{body}</div>;
}

export interface FeaturedListV2Props {
  title?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * FeaturedListV2 — horizontal featured rail under toolbar (§6.11 featuredList).
 */
export function FeaturedListV2({ title, children }: FeaturedListV2Props) {
  return (
    <section className="space-y-3">
      {title ? (
        <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400">
          {title}
        </p>
      ) : null}
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 snap-x snap-mandatory [scrollbar-width:thin]">
        {children}
      </div>
    </section>
  );
}
